import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAdmin } from '../contexts/AdminContext';
import apiService from '../services/api';
import { getImageUrl, getStableImageUrl } from '../utils/apiUrls';
import { TAXI_AGREMENT_BADGE, TAXI_PHONE_DISPLAY, TAXI_PHONE_TEL } from '../constants/branding';

const GoogleWidget = lazy(() => import('./GoogleWidget'));

const DEFAULT_HERO_IMAGE_PATH = '/hero-home.webp';
const HERO_PLACEHOLDER_SRC =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1 1'%3E%3C/svg%3E";

function formatNewsDate(dateValue) {
  if (!dateValue) return '';
  const d = new Date(dateValue);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('fr-FR');
}

const FAQ_ITEMS = [
  {
    question: "Quel est le tarif taxi de La Grand-Croix vers l'aéroport de Lyon ?",
    answer: "Le tarif estimé de La Grand-Croix vers l'aéroport Lyon-Saint Exupéry est d'environ 120 à 140€ selon l'heure (jour ou nuit). Tarifs officiels Loire : prise en charge 3,10€ + 2,18€/km de jour ou 3,27€/km de nuit."
  },
  {
    question: "TAXI ABEL est-il disponible 24h/24 et 7j/7 ?",
    answer: "Oui, notre service est disponible 24h/24 et 7j/7 incluant les week-ends et jours fériés. Que ce soit pour un vol de nuit, une hospitalisation urgente ou un trajet matinal en gare, nous sommes là."
  },
  {
    question: "Comment calculer le prix de ma course ?",
    answer: "Utilisez notre calculateur de tarifs en ligne pour obtenir une estimation instantanée. Renseignez votre adresse de départ, d'arrivée et la date/heure souhaitée. Le calcul se fait selon les tarifs officiels de l'arrêté préfectoral de la Loire."
  },
  {
    question: "Proposez-vous le transport médical vers le CHU de Saint-Étienne ?",
    answer: "Oui, nous assurons le transport médical vers le CHU de Saint-Étienne (Hôpital Nord, Bellevue, Métropole), les hôpitaux lyonnais et tous les centres médicaux de la région. Service adapté et ponctuel."
  },
  {
    question: "Quelles gares desservez-vous depuis La Grand-Croix ?",
    answer: "Nous desservons toutes les gares de la région : Gare de Saint-Étienne Châteaucreux, Gare de Rive-de-Gier, Gare de La Grand-Croix, Gare Part-Dieu Lyon. Ponctualité garantie pour vos correspondances."
  },
  {
    question: "Acceptez-vous plusieurs passagers et des bagages ?",
    answer: "Oui, nos véhicules peuvent accueillir jusqu'à 4 passagers avec bagages. Pour les groupes plus importants, contactez-nous. Supplément bagage : 2€/unité à partir de 2 valises. 5ème passager : 4€."
  }
];

const ZONES = [
  { name: 'Taxi Rive-de-Gier', slug: '/taxi-rive-de-gier', dist: '8 km' },
  { name: 'Taxi Saint-Étienne', slug: '/taxi-saint-etienne', dist: '12 km' },
  { name: 'Taxi Lorette', slug: '/taxi-lorette', dist: '5 km' },
  { name: 'Taxi L\'Horme', slug: '/taxi-l-horme', dist: '6 km' },
  { name: 'Taxi Saint-Chamond', slug: '/taxi-saint-chamond', dist: '15 km' },
  { name: 'Taxi Mornant', slug: '/taxi-mornant', dist: '18 km' },
  { name: 'Taxi Givors', slug: '/taxi-givors', dist: '20 km' },
  { name: 'Taxi Lyon', slug: '/taxi-lyon', dist: '40 km' }
];

const SERVICES_NAV = [
  { name: 'Navette Aéroport', slug: '/navette-aeroport' },
  { name: 'Taxi Aéroport Lyon', slug: '/taxi-aeroport-lyon' },
  { name: 'Taxi Aéroport Saint-Étienne', slug: '/taxi-aeroport-saint-etienne-boutheon' },
  { name: 'Transport Médical', slug: '/transport-medical' },
  { name: 'Taxi de Nuit', slug: '/taxi-nuit' },
  { name: 'Taxi Gare', slug: '/taxi-gare' },
  { name: 'Course Urgente', slug: '/course-urgente' },
  { name: 'Taxi Stations de Ski', slug: '/taxi-stations-ski' }
];

function FaqItem({ question, answer, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item" itemScope itemProp="mainEntity" itemType="https://schema.org/Question">
      <button
        className={`faq-question ${open ? 'open' : ''}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        itemProp="name"
      >
        <span>{question}</span>
        <span className="faq-icon">{open ? '−' : '+'}</span>
      </button>
      <div
        className={`faq-answer ${open ? 'open' : ''}`}
        itemScope
        itemProp="acceptedAnswer"
        itemType="https://schema.org/Answer"
      >
        <p itemProp="text">{answer}</p>
      </div>
    </div>
  );
}

function HomePage() {
  const { settings } = useAdmin();
  const [services, setServices] = useState([]);
  const [news, setNews] = useState([]);
  const [servicesLoaded, setServicesLoaded] = useState(false);
  const [newsLoaded, setNewsLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideWidth, setSlideWidth] = useState(296);
  const [currentNewsSlide, setCurrentNewsSlide] = useState(0);
  const [newsSlideWidth, setNewsSlideWidth] = useState(316);
  const loading = !servicesLoaded || !newsLoaded;

  useEffect(() => {
    loadServices();
    loadNews();

    const handleResize = () => {
      if (window.innerWidth <= 480) {
        setSlideWidth(236);
        setNewsSlideWidth(276);
      } else if (window.innerWidth <= 768) {
        setSlideWidth(266);
        setNewsSlideWidth(296);
      } else {
        setSlideWidth(296);
        setNewsSlideWidth(316);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadServices = async () => {
    try {
      const response = await apiService.getServices();
      setServices(response.services || []);
    } catch (error) {
      console.error('Erreur chargement services:', error);
    } finally {
      setServicesLoaded(true);
    }
  };

  const loadNews = async () => {
    try {
      const response = await apiService.getNews();
      if (response && response.news) {
        setNews(response.news.filter(item => !item.is_rss));
      } else {
        setNews([]);
      }
    } catch (error) {
      setNews([]);
    } finally {
      setNewsLoaded(true);
    }
  };

  const nextSlide = () => {
    const maxSlides = Math.max(0, services.length - 3);
    setCurrentSlide(prev => prev < maxSlides ? prev + 1 : 0);
  };

  const prevSlide = () => {
    const maxSlides = Math.max(0, services.length - 3);
    setCurrentSlide(prev => prev > 0 ? prev - 1 : maxSlides);
  };

  const nextNewsSlide = () => {
    const visibleItems = window.innerWidth <= 480 ? 1 : window.innerWidth <= 768 ? 2 : 3;
    const maxSlides = Math.max(0, news.length - visibleItems);
    setCurrentNewsSlide(prev => prev < maxSlides ? prev + 1 : 0);
  };

  const prevNewsSlide = () => {
    const visibleItems = window.innerWidth <= 480 ? 1 : window.innerWidth <= 768 ? 2 : 3;
    const maxSlides = Math.max(0, news.length - visibleItems);
    setCurrentNewsSlide(prev => prev > 0 ? prev - 1 : maxSlides);
  };

  const heroImageUrl = getStableImageUrl(DEFAULT_HERO_IMAGE_PATH);
  const displayPhone = settings.phone || TAXI_PHONE_DISPLAY;
  const displayPhoneTel = (settings.phone || TAXI_PHONE_TEL).replace(/[^\d+]/g, '');

  const organizationPhone = displayPhoneTel;
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'TaxiService',
    '@id': 'https://taxiabel.fr/#organization',
    name: settings.companyName || 'TAXI ABEL',
    alternateName: 'Taxi Abel La Grand-Croix',
    description: settings.companySlogan || 'Service de taxi professionnel 24h/24 et 7j/7 à La Grand-Croix, vallée du Gier, Saint-Étienne, Lyon et aéroports.',
    url: 'https://taxiabel.fr',
    telephone: organizationPhone,
    email: settings.email || 'contact@taxiabel.fr',
    logo: 'https://taxiabel.fr/logo192.png',
    image: heroImageUrl || 'https://taxiabel.fr/hero-home.webp',
    address: {
      '@type': 'PostalAddress',
      streetAddress: settings.address || 'La Grand-Croix',
      addressLocality: 'La Grand-Croix',
      postalCode: '42320',
      addressRegion: 'Auvergne-Rhône-Alpes',
      addressCountry: 'FR'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '45.5135',
      longitude: '4.6152'
    },
    areaServed: [
      { '@type': 'City', name: 'La Grand-Croix' },
      { '@type': 'City', name: 'Rive-de-Gier' },
      { '@type': 'City', name: 'Saint-Étienne' },
      { '@type': 'City', name: 'Lorette' },
      { '@type': 'City', name: "L'Horme" },
      { '@type': 'City', name: 'Saint-Chamond' },
      { '@type': 'City', name: 'Mornant' },
      { '@type': 'City', name: 'Givors' },
      { '@type': 'City', name: 'Lyon' }
    ],
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '00:00',
      closes: '23:59'
    },
    priceRange: '€€',
    currenciesAccepted: 'EUR',
    paymentAccepted: 'Cash, Credit Card',
    serviceType: ['Taxi', 'Navette aéroport', 'Transport médical', 'Taxi nuit']
  };

  const faqPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((faqItem) => ({
      '@type': 'Question',
      name: faqItem.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faqItem.answer
      }
    }))
  };

  return (
    <div itemScope itemType="https://schema.org/WebPage">
      <Helmet>
        <title>Taxi Abel La Grand-Croix — Taxi 24h/7j | Vallée du Gier, Lyon, Aéroport</title>
        <meta name="description" content="TAXI ABEL : taxi professionnel La Grand-Croix (42320). Navette aéroport Lyon, gares, CHU Saint-Étienne. Tarifs officiels Loire 2025. Réservation 24h/7j au 06 62 10 71 42." />
        <meta name="keywords" content="taxi La Grand-Croix, taxi vallée du Gier, taxi Rive-de-Gier, taxi Saint-Étienne, taxi Lyon, navette aéroport Lyon, transport médical CHU, taxi 24h Loire" />
        <link rel="canonical" href="https://taxiabel.fr/" />
        <meta property="og:title" content="TAXI ABEL — Taxi professionnel La Grand-Croix" />
        <meta property="og:description" content="Transport premium 24h/7j. Calculateur de tarifs officiels en ligne." />
        <meta property="og:url" content="https://taxiabel.fr/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={heroImageUrl || 'https://taxiabel.fr/hero-home.webp'} />
      </Helmet>
      {/* Hero Section — photo WebP desktop uniquement (mobile = gradient CSS, LCP = texte) */}
      <section
        className="hero hero-split hero-skeleton"
        aria-label="Présentation Taxi Abel"
      >
        <div className="hero-background-fallback" aria-hidden="true" />
        <picture>
          <source media="(min-width: 769px)" srcSet={heroImageUrl} type="image/webp" />
          <img
            className="hero-lcp-image"
            src={HERO_PLACEHOLDER_SRC}
            alt=""
            fetchPriority="high"
            decoding="async"
            width="1024"
            height="682"
            aria-hidden="true"
          />
        </picture>
        <div className="hero-overlay"></div>
        <div className="container">
          <div className="hero-split-grid">
            <div className="hero-content">
              <p className="hero-eyebrow">{settings.heroEyebrow || TAXI_AGREMENT_BADGE}</p>
              <h1>{settings.heroTitle || settings.companyName || 'TAXI ABEL'}</h1>
              <p className="hero-subtitle">{settings.heroSubtitle || settings.companySlogan || 'Votre taxi premium — vallée du Gier & Lyon'}</p>
              <p className="hero-description">
                {settings.heroDescription || `Transport professionnel 24h/24 depuis ${settings.address || 'La Grand-Croix'}. Aéroports, gares, CHU, ski et urgences — tarifs officiels Loire 2025.`}
              </p>
              <div className="hero-badges">
                <span className="badge">24h/7j</span>
                <span className="badge">Tarifs officiels</span>
                <span className="badge">4.9★ Google</span>
              </div>
              <div className="hero-buttons">
                <Link to="/calculateur" className="btn-hero-primary">
                  Calculer un tarif
                </Link>
                <a href={`tel:${displayPhoneTel}`} className="btn-hero-secondary">
                  {settings.button2Text || displayPhone}
                </a>
              </div>
            </div>
            <aside className="hero-booking-card" aria-label="Réservation rapide">
              <h2 className="hero-booking-title">Réserver en 30 secondes</h2>
              <div className="hero-stats">
                <div className="hero-stat"><strong>24/7</strong><span>Disponible</span></div>
                <div className="hero-stat"><strong>3,10€</strong><span>Prise en charge</span></div>
                <div className="hero-stat"><strong>2,18€</strong><span>/km jour</span></div>
                <div className="hero-stat"><strong>3,27€</strong><span>/km nuit</span></div>
              </div>
              <p className="hero-card-note">Arrêté préfectoral Loire 57-DDPP-2025 — estimation instantanée en ligne</p>
              <Link to="/calculateur" className="btn-hero-primary btn-block">Obtenir mon tarif</Link>
              <a href={`tel:${displayPhoneTel}`} className="hero-card-phone">
                Ou appelez {displayPhone}
              </a>
            </aside>
          </div>
        </div>
      </section>

      {/* Bandeau confiance */}
      <section className="trust-bar" aria-label="Indicateurs de confiance">
        <div className="container">
          <div className="trust-items">
            <div className="trust-item">
              <span className="trust-icon">⭐</span>
              <span>Tarifs officiels Arrêté Préfectoral Loire 2025</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">🕐</span>
              <span>Disponible 24h/24, 7j/7</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">🚕</span>
              <span>Chauffeur professionnel agréé</span>
            </div>
            <div className="trust-item">
              <span className="trust-icon">📍</span>
              <span>La Grand-Croix & toute la vallée du Gier</span>
            </div>
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="steps-section section-block alt" aria-label="Comment réserver">
        <div className="container">
          <div className="section-header">
            <p className="section-eyebrow">Simple & rapide</p>
            <h2>Réserver votre taxi en 3 étapes</h2>
          </div>
          <div className="steps-grid">
            <div className="step-card">
              <span className="step-num">01</span>
              <h3>Indiquez votre trajet</h3>
              <p>Adresse départ et arrivée via notre calculateur ou par téléphone.</p>
            </div>
            <div className="step-card">
              <span className="step-num">02</span>
              <h3>Obtenez votre tarif</h3>
              <p>Estimation selon arrêté préfectoral Loire — transparent et officiel.</p>
            </div>
            <div className="step-card">
              <span className="step-num">03</span>
              <h3>On vient vous chercher</h3>
              <p>Chauffeur agréé, ponctuel, 24h/24 y compris jours fériés.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="services section-block" aria-label="Nos services taxi">
        <div className="container">
          <div className="section-header">
            <p className="section-eyebrow">Nos expertises</p>
            <h2>Nos Services de Transport</h2>
            <p className="lead">
              Solutions professionnelles — aéroports, gares, hôpitaux, ski et urgences
            </p>
          </div>

          {loading ? (
            <div className="text-center">
              <p>Chargement des services...</p>
            </div>
          ) : services.length > 0 ? (
            <div className="services-carousel">
              <div
                className="services-carousel-container"
                style={{ transform: `translateX(-${currentSlide * slideWidth}px)` }}
              >
                {services.map((service) => (
                  <div key={service.id} className="services-carousel-item">
                    <div className="card service-card">
                      <div className="service-icon">{service.icon}</div>
                      <h3>{service.title}</h3>
                      <p>{service.description}</p>
                      {service.image_url && (
                        <div className="service-image">
                          <img
                            src={getImageUrl(service.image_url)}
                            loading="lazy"
                            decoding="async"
                            alt={`${service.title} - Taxi Abel La Grand-Croix`}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {services.length > 3 && (
                <>
                  <div className="carousel-nav">
                    <button className="carousel-btn" onClick={prevSlide} disabled={currentSlide === 0} aria-label="Service précédent">‹</button>
                    <button className="carousel-btn" onClick={nextSlide} disabled={currentSlide >= Math.max(0, services.length - 3)} aria-label="Service suivant">›</button>
                  </div>
                  <div className="carousel-dots">
                    {Array.from({ length: Math.max(1, services.length - 2) }).map((_, index) => (
                      <button key={index} className={`carousel-dot ${currentSlide === index ? 'active' : ''}`} onClick={() => setCurrentSlide(index)} aria-label={`Service ${index + 1}`} />
                    ))}
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="bento-services">
              {[
                { icon: '✈️', title: 'Navette Aéroport', desc: 'Lyon-Saint Exupéry, Saint-Étienne-Bouthéon, Grenoble-Isère. Réservation 24h/24, tarifs forfaitaires.', link: '/navette-aeroport' },
                { icon: '🏥', title: 'Transport Médical', desc: 'CHU Saint-Étienne, hôpitaux lyonnais, centres médicaux. Service adapté et ponctuel.', link: '/transport-medical' },
                { icon: '🚉', title: 'Taxi Gare', desc: 'Châteaucreux, Part-Dieu, Rive-de-Gier. Ponctualité garantie pour vos correspondances.', link: '/taxi-gare' },
                { icon: '🌙', title: 'Taxi de Nuit', desc: 'Service disponible toute la nuit. Transport sécurisé et fiable jusqu\'à l\'aube.', link: '/taxi-nuit' },
                { icon: '⛷️', title: 'Taxi Stations de Ski', desc: 'Chalmazel, Les Saisies, La Clusaz. Départ depuis La Grand-Croix et la vallée du Gier.', link: '/taxi-stations-ski' },
                { icon: '🚨', title: 'Course Urgente', desc: 'Intervention rapide pour vos besoins urgents de transport. Réponse immédiate.', link: '/course-urgente' }
              ].map((s, i) => (
                <a key={i} href={s.link} className={`bento-service-card${i === 0 ? ' bento-featured' : ''}`}>
                  <span className="service-icon-lg">{s.icon}</span>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <span className="bento-arrow">→</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Section Zones desservies */}
      <section className="zones-section" aria-label="Zones desservies">
        <div className="container">
          <h2 className="text-center mb-2">Zones desservies par Taxi Abel</h2>
          <p className="lead text-center mb-4">
            Basé à La Grand-Croix, nous couvrons toute la vallée du Gier et la région Auvergne-Rhône-Alpes
          </p>
          <div className="zones-grid">
            {ZONES.map((zone, index) => (
              <Link key={index} to={zone.slug} className="zone-card">
                <span className="zone-icon">📍</span>
                <span className="zone-name">{zone.name}</span>
                <span className="zone-dist">{zone.dist}</span>
              </Link>
            ))}
          </div>
          <div className="zones-extra">
            <h3>Également desservies</h3>
            <p>
              Firminy, Unieux, Le Chambon-Feugerolles, Roche-la-Molière, Sorbiers, Saint-Priest-en-Jarez,
              Farnay, Châteauneuf, Rive-de-Gier, Vienne, Condrieu, Pélussin, Ampuis, Chasse-sur-Rhône,
              Givors, Beauvallon, Saint-Genis-Laval, Oullins, et toute l'agglomération lyonnaise.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="cta-band" aria-label="Calculateur de tarifs">
        <div className="container">
          <h2>Calculateur de tarifs officiels</h2>
          <p>Estimation instantanée — Arrêté préfectoral Loire 57-DDPP-2025</p>
          <Link to="/calculateur" className="btn-hero-primary">
            Calculer mon tarif
          </Link>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section" aria-label="Contact">
        <div className="container">
          <h2>Réserver votre taxi maintenant</h2>
          <div className="contact-grid">
            <div className="contact-item">
              <h3>📞 Téléphone</h3>
              <a href={`tel:${settings.phone || '+33477123456'}`}>
                {settings.phone || '04 77 12 34 56'}
              </a>
              <p>Disponible 24h/7j</p>
            </div>

            <div className="contact-item">
              <h3>💬 WhatsApp</h3>
              <a href={`https://wa.me/${(settings.phone || '+33477123456').replace(/[^\d]/g, '')}`} target="_blank" rel="noopener noreferrer">
                Envoyer un message
              </a>
              <p>Réponse rapide garantie</p>
            </div>

            <div className="contact-item">
              <h3>✉️ Email</h3>
              <a href={`mailto:${settings.email || 'contact@taxiabel.fr'}`}>
                {settings.email || 'contact@taxiabel.fr'}
              </a>
              <p>Pour les réservations groupes</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section section-block alt" aria-label="Questions fréquentes" itemScope itemType="https://schema.org/FAQPage">
        <div className="container">
          <div className="section-header">
            <p className="section-eyebrow">FAQ</p>
            <h2>Questions fréquentes</h2>
            <p className="lead">Tout savoir sur notre service taxi</p>
          </div>
          <div className="faq-list">
            {FAQ_ITEMS.map((item, index) => (
              <FaqItem key={index} question={item.question} answer={item.answer} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* News Section */}
      {news.length > 0 && (
        <section className="section" aria-label="Actualités">
          <div className="container">
            <h2 className="text-center mb-4">Actualités</h2>
            <div className="news-carousel">
              <div
                className="news-carousel-container"
                style={{ transform: `translateX(-${currentNewsSlide * newsSlideWidth}px)` }}
              >
                {news.map((item) => (
                  <div key={item.id} className="news-carousel-item">
                    <article className="card news-card">
                      {item.image_url && (
                        <div className="news-image">
                          <img
                            src={getImageUrl(item.image_url)}
                            loading="lazy"
                            decoding="async"
                            alt={item.title}
                            onError={(e) => { e.target.style.display = 'none'; }}
                          />
                        </div>
                      )}
                      <h3>{item.title}</h3>
                      <p className="news-description">{item.description}</p>
                      <div className="news-meta">
                        {formatNewsDate(item.created_at) && (
                          <time dateTime={item.created_at} className="news-date">{formatNewsDate(item.created_at)}</time>
                        )}
                      </div>
                    </article>
                  </div>
                ))}
              </div>

              {news.length > 3 && (
                <>
                  <div className="carousel-nav">
                    <button className="carousel-btn" onClick={prevNewsSlide} disabled={currentNewsSlide === 0} aria-label="Actualité précédente">‹</button>
                    <button className="carousel-btn" onClick={nextNewsSlide} disabled={currentNewsSlide >= Math.max(0, news.length - 3)} aria-label="Actualité suivante">›</button>
                  </div>
                  <div className="carousel-dots">
                    {Array.from({ length: Math.max(1, news.length - 2) }).map((_, index) => (
                      <button key={index} className={`carousel-dot ${currentNewsSlide === index ? 'active' : ''}`} onClick={() => setCurrentNewsSlide(index)} aria-label={`Actualité ${index + 1}`} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Liens services SEO */}
      <section className="services-links-section">
        <div className="container">
          <h2 className="text-center mb-3">Tous nos services spécialisés</h2>
          <div className="services-links-grid">
            {SERVICES_NAV.map((serviceLink, index) => (
              <Link key={index} to={serviceLink.slug} className="service-link-pill">
                {serviceLink.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <a href={`tel:${settings.phone || '+33477123456'}`} className="magix-fab" aria-label="Appeler Taxi Abel">📞</a>

      {/* Google Widget Natif — chargé après le contenu principal */}
      <Suspense fallback={null}>
        <GoogleWidget />
      </Suspense>

      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqPageSchema)}</script>
    </div>
  );
}

export default HomePage;
